import { useCallback, useEffect, useState } from 'react';
import { logger } from '../logger';
import { useI18nContext } from '../providers/I18nProvider';
import {
  getTranslationRevision,
  getTranslations as getTranslationsFromService,
  subscribeTranslationRevision,
} from '../services/translationService';
import { FlatTranslationKey, SupportedLanguage, TranslationKeys } from '../types/i18n';

import { useAppStore } from '../stores/appStore';

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: any, path: string): string {
  return path.split('.').reduce((current, key) => current?.[key], obj) || path;
}

/**
 * Hook for managing translations and language switching
 */
export const useTranslation = (overrideIsAdha?: boolean) => {
  const { currentLanguage, isLoading, changeLanguage } = useI18nContext();
  const isGlobalAdha = useAppStore((state) => state.isAdhaJourney);
  const isAdha = overrideIsAdha ?? isGlobalAdha;
  const [translationRevision, setTranslationRevision] = useState(getTranslationRevision);

  useEffect(() => {
    return subscribeTranslationRevision(() => {
      setTranslationRevision(getTranslationRevision());
    });
  }, []);

  // Get translation for a key from Contentful-aware translation service
  const t = useCallback((key: FlatTranslationKey): string => {
    const currentTranslations = getTranslationsFromService(currentLanguage);

    if (isAdha) {
      const adhaKey = `adha.${key}` as FlatTranslationKey;
      const adhaVal = getNestedValue(currentTranslations, adhaKey);
      if (adhaVal && adhaVal !== adhaKey) {
        return adhaVal;
      }
    }

    return getNestedValue(currentTranslations, key);
  }, [currentLanguage, isAdha, translationRevision]);

  // Get all translations for current language from Contentful-aware translation service
  const getTranslations = useCallback((): TranslationKeys => {
    return getTranslationsFromService(currentLanguage);
  }, [currentLanguage, translationRevision]);

  // Toggle between languages
  const toggleCurrentLanguage = useCallback(async () => {
    try {
      const newLanguage: SupportedLanguage = currentLanguage === 'en' ? 'ar' : 'en';
      await changeLanguage(newLanguage);
      return newLanguage;
    } catch (error) {
      logger.error('useTranslation', 'Failed to toggle language:', error);
      throw error;
    }
  }, [currentLanguage, changeLanguage]);

  return {
    t,
    currentLanguage,
    isLoading,
    getTranslations,
    changeLanguage,
    toggleLanguage: toggleCurrentLanguage,
  };
};